import { Params } from "@remix-run/router";
import { appQueryClient } from "app/AppQueryClient";
import { createInvestorEntityRoutes } from "features/investor-entities/InvestorEntityRoutes";
import { getCapitalActivityTableDataRetrieveLoader } from "gen/endpoints/capital-activity/capital-activity";
import { getClosingListLoader } from "gen/endpoints/closing/closing";
import { getClosingTableRetrieveLoader } from "gen/endpoints/closing-table-data/closing-table-data";
import {
  commitmentInformationRetrieve,
  getCommitmentInformationRetrieveLoader,
  getCommitmentInformationRetrieveQueryKey,
} from "gen/endpoints/commitment-information/commitment-information";
import { getCommitmentInformationTableDataRetrieveLoader } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import {
  getCommitmentStatsRetrieveLoader,
  getFundFamilyCommitmentStatsRetrieveLoader,
} from "gen/endpoints/commitment-stats/commitment-stats";
import { getFeesAndCarryRetrieveLoader } from "gen/endpoints/fees-and-carry/fees-and-carry";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getSecurityClassListLoader } from "gen/endpoints/fund-security-classes/fund-security-classes";
import { getInvestorClassListLoader } from "gen/endpoints/investor-class/investor-class";
import { getSecurityOwnershipRetrieveLoader } from "gen/endpoints/security-ownership/security-ownership";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { NonNullableFields } from "shared/types/NonNullableFields";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const createCommitmentRoutes = (
  parent: string,
  path = "commitments",
  includeIndex = true,
): RouteObject => {
  const idPrefix = `${parent}Commitments`;
  return {
    id: idPrefix,
    path,
    children: [
      ...(includeIndex
        ? [
            {
              id: `${idPrefix}Index`,
              handle: {
                showSidebar: true,
              },
              lazy: async () => {
                const { CommitmentsPage } = await import(
                  "features/commitments/CommitmentsPage"
                );
                return { Component: CommitmentsPage };
              },
              children: [
                {
                  id: `${idPrefix}Tab`,
                  index: true,
                  path: "",
                  lazy: async () => {
                    const { CommitmentsTabCommitments } = await import(
                      "features/commitments/tabs/CommitmentsTabCommitments"
                    );
                    return { Component: CommitmentsTabCommitments };
                  },
                  loader: composeLoaders(
                    getFundFamilyCommitmentStatsRetrieveLoader(),
                    getClosingListLoader({ is_draft: true }),
                    getCommitmentInformationTableDataRetrieveLoader(
                      ({ fundFamilyId }) => ({
                        fund_family_id: fundFamilyId,
                      }),
                    ),
                  ),
                  handle: {
                    title: i18n.t("common:commitments"),
                  },
                },
                {
                  id: `${idPrefix}ClosingsTab`,
                  path: "closings",
                  lazy: async () => {
                    const { CommitmentsTabClosings } = await import(
                      "features/commitments/tabs/CommitmentsTabClosings"
                    );
                    return { Component: CommitmentsTabClosings };
                  },
                  loader: composeLoaders(
                    getClosingTableRetrieveLoader(),
                    getClosingListLoader({ is_draft: true }),
                    getCommitmentInformationTableDataRetrieveLoader(
                      ({ fundFamilyId }) => ({
                        fund_family_id: fundFamilyId,
                        is_unattached_to_closing: true,
                      }),
                    ),
                  ),
                  handle: {
                    title: i18n.t("common:closings"),
                  },
                },
                {
                  id: `${idPrefix}TransfersTab`,
                  path: "transfers",
                  handle: {
                    title: i18n.t("common:transfers"),
                  },
                  lazy: async () => {
                    const { CommitmentsTabTransfers } = await import(
                      "features/commitments/tabs/CommitmentsTabTransfers"
                    );
                    return { Component: CommitmentsTabTransfers };
                  },
                },
              ],
            },
          ]
        : []),
      {
        id: `${idPrefix}Fee`,
        path: ":commitmentId/fees/:feeId",
        lazy: async () => {
          const { CommitmentFeePage } = await import(
            "features/commitments/fees/CommitmentFeePage"
          );
          return { Component: CommitmentFeePage };
        },
      },
      {
        id: `${idPrefix}FeeOverride`,
        path: ":commitmentId/fees/:feeId/override",
        lazy: async () => {
          const { CommitmentFeeOverridePage } = await import(
            "features/commitments/fees/CommitmentFeeOverridePage"
          );
          return { Component: CommitmentFeeOverridePage };
        },
      },
      {
        id: `${idPrefix}Commitment`,
        path: ":commitmentId/",
        lazy: async () => {
          const { CommitmentPage } = await import(
            "features/commitments/view/CommitmentPage"
          );
          return { Component: CommitmentPage };
        },
        loader: getCommitmentInformationRetrieveLoader(),
        handle: {
          idPrefix,
        },
        children: [
          {
            id: `${idPrefix}CommitmentActivity`,
            path: "activity",
            lazy: async () => {
              const { CommitmentTabActivity } = await import(
                "features/commitments/view/tabs/CommitmentTabActivity"
              );
              return { Component: CommitmentTabActivity };
            },
            loader: async (args) => {
              const { fundFamilyId, commitmentId } =
                args.params as NonNullableFields<Params>;
              const commitment = await appQueryClient.ensureQueryData({
                queryKey: getCommitmentInformationRetrieveQueryKey(
                  fundFamilyId,
                  commitmentId,
                ),
                queryFn: () =>
                  commitmentInformationRetrieve(fundFamilyId, commitmentId),
              });
              const params = {
                commitment_id: commitmentId,
                fund_family_ids: [fundFamilyId],
                investor_entity_id: commitment.owning_entity_id,
              };
              return composeLoaders(
                getFundListLoader({
                  investor_entity_id: params.investor_entity_id,
                }),
                getCommitmentStatsRetrieveLoader(params),
                getFeesAndCarryRetrieveLoader(params),
                getCapitalActivityTableDataRetrieveLoader(params),
              )(args);
            },
            handle: {
              title: i18n.t("commitment:tabs.activity.title"),
            },
          },
          {
            id: `${idPrefix}CommitmentProfile`,
            path: "profile",
            lazy: async () => {
              const { CommitmentTabProfile } = await import(
                "features/commitments/view/tabs/CommitmentTabProfile"
              );
              return { Component: CommitmentTabProfile };
            },
            loader: composeLoaders(
              getSecurityClassListLoader(),
              getInvestorClassListLoader(),
              getSecurityOwnershipRetrieveLoader(
                ({ fundFamilyId, commitmentId }) => ({
                  fundFamilyId: fundFamilyId!,
                  id: commitmentId!,
                }),
              ),
            ),
            handle: {
              title: i18n.t("commitment:tabs.profile.title"),
            },
          },
          {
            id: `${idPrefix}CommitmentFees`,
            path: "fees",
            lazy: async () => {
              const { CommitmentTabFees } = await import(
                "features/commitments/view/tabs/CommitmentTabFees"
              );
              return { Component: CommitmentTabFees };
            },
            handle: {
              title: i18n.t("commitment:tabs.fees.title"),
            },
          },
          {
            id: `${idPrefix}CommitmentWaterfall`,
            path: "waterfall",
            lazy: async () => {
              const { CommitmentSetupTabWaterfall } = await import(
                "features/commitments/setup/CommitmentSetupTabWaterfall"
              );
              return { Component: CommitmentSetupTabWaterfall };
            },
            handle: {
              title: i18n.t("commitment:tabs.waterfall.title"),
            },
          },
          {
            id: `${idPrefix}CommitmentPerformance`,
            path: "performance",
            lazy: async () => {
              const { HypotheticalPerformanceTab } = await import(
                "features/financial-reporting/HypotheticalPerformanceTab"
              );
              return { Component: HypotheticalPerformanceTab };
            },
            handle: {
              title: i18n.t("commitment:tabs.performance.title"),
            },
          },
        ],
      },
      {
        id: `${idPrefix}CancelCorrect`,
        path: "cancel-correct/:commitmentId/:fundFamilyEventId/*",
        lazy: async () => {
          const { CommitmentCancelCorrectPage } = await import(
            "features/commitments/cancel-correct/CommitmentCancelCorrectPage"
          );
          return { Component: CommitmentCancelCorrectPage };
        },
        children: [
          {
            id: `${idPrefix}CancelCorrectClass`,
            path: "",
            lazy: async () => {
              const { CommitmentCancelCorrectClassPage } = await import(
                "features/commitments/cancel-correct/CommitmentCancelCorrectClassPage"
              );
              return { Component: CommitmentCancelCorrectClassPage };
            },
            handle: {
              title: i18n.t("commitment:cancelCorrect.selectNewClass"),
              breadcrumb: i18n.t("commitment:cancelCorrect.selectClass"),
            },
          },
          {
            id: `${idPrefix}CancelCorrectFeeTrueUp`,
            path: "fee-true-up",
            lazy: async () => {
              const { CommitmentCancelCorrectFeeTrueUpPage } = await import(
                "features/commitments/cancel-correct/CommitmentCancelCorrectFeeTrueUpPage"
              );
              return { Component: CommitmentCancelCorrectFeeTrueUpPage };
            },
            handle: {
              title: i18n.t("commitment:cancelCorrect.feeTrueUp.title"),
              breadcrumb: i18n.t(
                "commitment:cancelCorrect.feeTrueUp.breadcrumb",
              ),
            },
          },
          {
            id: `${idPrefix}CancelCorrectEqualization`,
            path: "equalization",
            lazy: async () => {
              const { CommitmentCancelCorrectEqualizationPage } = await import(
                "features/commitments/cancel-correct/CommitmentCancelCorrectEqualizationPage"
              );
              return { Component: CommitmentCancelCorrectEqualizationPage };
            },
            handle: {
              title: i18n.t("commitment:cancelCorrect.equalization.title"),
              breadcrumb: i18n.t(
                "commitment:cancelCorrect.equalization.breadcrumb",
              ),
            },
          },
        ],
      },
      {
        id: `${idPrefix}CancelCorrectHistorical`,
        path: "cancel-correct/:commitmentId/:fundFamilyEventId/historical",
        lazy: async () => {
          const { CommitmentCancelCorrectHistoricalPage } = await import(
            "features/commitments/cancel-correct/CommitmentCancelCorrectHistoricalPage"
          );
          return { Component: CommitmentCancelCorrectHistoricalPage };
        },
        handle: {
          title: i18n.t("commitment:cancelCorrect.cancelCorrect"),
        },
      },
      ...(!idPrefix.includes("CommitmentsInvestorEntities")
        ? [createInvestorEntityRoutes(idPrefix)]
        : []),
    ],
  };
};
